// OG-faithful SDMS "Recalculate" helpers. Pure (no React / Univer / network) so they unit-test in
// isolation. OG Recalculate (SpreadsheetEntryController :26057) reloads the PRISTINE template and
// re-pushes EVERY template formula cell into the live sheet — header cells as-is, detail-band
// cells copied down over all data rows — skipping the FIELD merge cells (DisplayText starting
// '['); typed values are untouched. That both restores a formula the analyst typed over AND
// strips any Apply-Rounding `_LIMSRound` wrap. The sheet-level passes (restoreTemplateFormulas +
// stripLimsRoundWraps) live in SdmsResultEntryPage and call these.
import { isSampleQcSplitSheet, SAMPLE_QC_SPLIT_MODEL } from './sdmsSampleQcSplit';

/**
 * Is this pristine-template cell one OG's Recalculate re-pushes? OG takes every cell with a
 * formula whose DisplayText is empty or does not start with '[' — i.e. it skips the pure FIELD
 * merge cells (they display "[NUMERICRESULT]"). For AI two more classes must be skipped because
 * the merge has already SPLICED their FIELD()/[[…]] references into literals: re-pushing the raw
 * template text would put an unresolvable FIELD(...) back into the sheet. (2026-09-22)
 */
export function isRepushableTemplateFormula(expr: string | null | undefined, cachedText: string | null | undefined): boolean {
  const f = (expr ?? '').trim();
  if (!f) return false;
  if (/FIELD\s*\(/i.test(f) || /\[\[/.test(f)) return false;   // merge-bound — resolved by the merge, not the template
  const t = (cachedText ?? '').trim();
  return !(t.length > 0 && t[0] === '[');                     // OG's DisplayText[0] != '[' rule
}

/**
 * Extract the inner (first) argument of a `_LIMSRound(inner, sig, cut, dec, rpt, def)` expression
 * (ExcelJS gives the expression WITHOUT a leading '='), respecting nested parens and quoted
 * strings inside `inner` (it is usually an IF()/AVERAGEIFS() carrying its own commas). Returns the
 * restored formula `=<inner>`, or null when the expression is not a `_LIMSRound` wrap.
 */
export function unwrapLimsRound(expr: string): string | null {
  const m = /^\s*=?\s*_LIMSRound\s*\(/i.exec(expr);
  if (!m) return null;
  let depth = 0, inQuote = false, i = m[0].length;
  const start = i;
  for (; i < expr.length; i++) {
    const ch = expr[i];
    if (inQuote) { if (ch === '"') inQuote = false; continue; }
    if (ch === '"') inQuote = true;
    else if (ch === '(') depth++;
    else if (ch === ')') { if (depth === 0) break; depth--; }
    else if (ch === ',' && depth === 0) break;   // first-arg (inner) separator
  }
  const inner = expr.substring(start, i).trim();
  return inner.length ? `=${inner}` : null;
}

/** The run codes (SysSampleCode) of the merge records, in record order, blanks dropped. The merge SPs
 *  name the column `SYSSamplecode`, so the key is matched case-insensitively: reading exactly
 *  `SYSSAMPLECODE` / `SysSampleCode` found nothing, so on a Model 1 template (VOC) Apply Rounding,
 *  rounding-on-save and Recalculate touched no run sheet and the Report got no runs (OG vs AI VOC
 *  comparison 2026-09-29: 0 `_LIMSRound` wraps saved vs OG's 446). */
export function runCodesOf(records: readonly Record<string, unknown>[] | null | undefined): string[] {
  const out: string[] = [];
  for (const r of records ?? []) {
    const key = Object.keys(r).find(k => k.toUpperCase() === 'SYSSAMPLECODE');
    const code = key === undefined ? '' : String(r[key] ?? '').trim();
    if (code) out.push(code);
  }
  return out;
}

/**
 * Which live worksheets OG's Apply Rounding / Recalculate act on (SpreadsheetEntryController
 * ApplyRounding :26197 / Recalculate :26072): Model 0 (BatchSampleEntry) -> the ONE main sheet;
 * Model 1 (SampleSheetNavigation) -> the per-run sheets named by each run's SysSampleCode; Model 2
 * (CalibrationSet) -> nothing. NEVER "every sheet not in an exclusion list": 11 templates carry extra
 * result-like sheets (Colilert/HPC/Tecta IDEXXMPNTable, THM Summary2, the MDL study sheets...) that
 * would get the main sheet's formulas/rounding pushed onto them, and THM's MAIN sheet is itself
 * named "Summary" -- an excluded name -- so the list skipped the real sheet. The main sheet is
 * identified by its TEMPLATE name (the merge keeps it); for Model 1 the main sheet is removed and
 * the run copies are keyed by run code. (cross-template audit 2026-09-22)
 */
export function resultSheetSelector(model: number, templateMainName: string | null, runCodes: readonly string[]): (sheetName: string) => boolean {
  if (model === 2) return () => false;
  // Model 4 (SampleQCSplit): OG Recalculate / ApplyRounding / ApplyEngineRounding / CoerceReportedCellsNumeric /
  // AddQcCellComments all act on exactly the two result sheets (:27865 / :27965 / :28129 / :28280 / :28447).
  if (model === SAMPLE_QC_SPLIT_MODEL) return isSampleQcSplitSheet;
  if (model === 1) {
    const runs = new Set(runCodes.map(c => c.trim().toUpperCase()).filter(Boolean));
    return (name) => runs.has(name.trim().toUpperCase());
  }
  const main = (templateMainName ?? '').trim().toUpperCase();
  return (name) => main.length > 0 && name.trim().toUpperCase() === main;
}

